/**
 * Status Configuration Utilities
 * 
 * Functions for getting status configuration and checking status categories.
 */

import { DEFAULT_STATUSES, STATUS_CATEGORIES } from '../TaskNode/constants';

/** When status id or stored object lacks `category`, map known ids (legacy / API gaps). */
const LEGACY_STATUS_ID_TO_CATEGORY = {
  pending: 'not_started',
  in_progress: 'in_progress',
  'in-progress': 'in_progress',
  completed: 'completed',
  done: 'completed',
  blocked: 'blocked',
};

/**
 * Best-effort category when `category` is missing on a custom status (migration / bad saves).
 */
function inferCategoryFromStatusName(name) {
  if (!name || typeof name !== 'string') return null;
  const n = name.toLowerCase().replace(/[\s_-]/g, '');
  if (/(done|complete|finished|closed|archived)/.test(n)) return 'completed';
  if (/(blocked|onhold|waiting|stuck)/.test(n)) return 'blocked';
  if (/(inprogress|progress|working|active|started|doing|develop)/.test(n)) return 'in_progress';
  if (/(todo|pending|backlog|planned|notstarted)/.test(n)) return 'not_started';
  return null;
}

/**
 * Get the category for a given status ID
 * @param {string} statusId - The status ID (e.g., 'in_design', 'pending')
 * @param {Array} statuses - The campaign's custom statuses array
 * @returns {string} The category ID ('not_started', 'in_progress', 'completed', 'blocked')
 */
export const getStatusCategory = (statusId, statuses = DEFAULT_STATUSES) => {
  if (!statusId) return 'not_started';
  const list = Array.isArray(statuses) && statuses.length ? statuses : DEFAULT_STATUSES;
  const status = list.find((s) => s.id === statusId);
  if (status?.category) return status.category;
  if (LEGACY_STATUS_ID_TO_CATEGORY[statusId]) return LEGACY_STATUS_ID_TO_CATEGORY[statusId];
  if (status?.name) {
    const inferred = inferCategoryFromStatusName(status.name);
    if (inferred) return inferred;
  }
  return 'not_started';
};

/**
 * Get the full status config for a given status ID
 * @param {string} statusId - The status ID
 * @param {Array} statuses - The campaign's custom statuses array
 * @returns {Object} The status config { id, name, category, color, order }
 */
export const getStatusConfig = (statusId, statuses = DEFAULT_STATUSES) => {
  const status = statuses.find(s => s.id === statusId);
  if (status) return status;
  
  // Fallback to first status or a default
  return statuses[0] || DEFAULT_STATUSES[0];
};

/**
 * Convert status config to the legacy STATUS_CONFIG format
 * Used for backwards compatibility with existing components
 * @param {Object} status - Status config { id, name, category, color }
 * @returns {Object} Legacy format { label, color, bg }
 */
export const toLegacyStatusConfig = (status) => {
  if (!status) {
    return { label: 'To Do', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)' };
  }
  
  // Convert hex color to rgba for background
  const hexToRgba = (hex, alpha = 0.15) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  return {
    label: status.name,
    color: status.color,
    bg: hexToRgba(status.color, 0.15),
  };
};

/**
 * Check if a task is in a "completed" category
 * @param {string} statusId - The status ID
 * @param {Array} statuses - The campaign's custom statuses array
 * @returns {boolean}
 */
export const isStatusCompleted = (statusId, statuses = DEFAULT_STATUSES) => {
  return getStatusCategory(statusId, statuses) === 'completed';
};

/**
 * Check if a task is in an "in_progress" category
 * @param {string} statusId - The status ID
 * @param {Array} statuses - The campaign's custom statuses array
 * @returns {boolean}
 */
export const isStatusInProgress = (statusId, statuses = DEFAULT_STATUSES) => {
  return getStatusCategory(statusId, statuses) === 'in_progress';
};

/**
 * Check if a task is in a "not_started" category
 * @param {string} statusId - The status ID
 * @param {Array} statuses - The campaign's custom statuses array
 * @returns {boolean}
 */
export const isStatusNotStarted = (statusId, statuses = DEFAULT_STATUSES) => {
  return getStatusCategory(statusId, statuses) === 'not_started';
};

/**
 * Check if a task is in a "blocked" category
 * @param {string} statusId - The status ID
 * @param {Array} statuses - The campaign's custom statuses array
 * @returns {boolean}
 */
export const isStatusBlocked = (statusId, statuses = DEFAULT_STATUSES) => {
  return getStatusCategory(statusId, statuses) === 'blocked';
};

/**
 * Get the category config
 * @param {string} categoryId - Category ID
 * @returns {Object} Category config { id, label, color }
 */
export const getCategoryConfig = (categoryId) => {
  return STATUS_CATEGORIES[categoryId] || STATUS_CATEGORIES.not_started;
};
